var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Box } from '@mui/material';
import React from 'react';
import { themeColors } from '../JuvareTheme';
const rotate = 'rotate(2deg)';
// This global flag is used to ensure that only a single card can be dragged at one time.
// Without this on a mobile device it would be possible to drag multiple cards with multiple fingers.
let draggingFlag = false;
/**
 * Renders a draggable card component with pointer-based drag and drop interactions.
 *
 * @param {DraggableCardProps} props - Component props.
 * @param {SxProps<Theme> | SxProps<Theme>[]} [props.sx] - Optional styles to apply to the card.
 * @param {boolean} [props.moving] - Indicates whether the card is in keyboard-reorder "moving" mode.
 * @param {(moving: boolean) => void} [props.onSetMoving] - Callback to set the moving state for keyboard-based reordering.
 * @param {(direction: 'left' | 'right') => void} [props.onMoveCard] - Callback for arrow-key based reordering.
 * @param {(point: { x: number; y: number }) => boolean} [props.onPointerDragStart] - Called on pointer down. Return `true` to start dragging.
 * @param {(point: { x: number; y: number }) => void} [props.onPointerDragMove] - Called during pointer movement while dragging.
 * @param {(point: { x: number; y: number }) => void} [props.onPointerDragEnd] - Called when dragging ends via pointer up or release.
 * @param {() => void} [props.onPointerDragCancel] - Called when dragging is cancelled (e.g., blur or visibility change).
 * @param {(card: React.ReactNode, sx: SxProps<Theme> | SxProps<Theme>[]) => React.ReactNode} [props.renderDragCard] - Optional custom rendering logic for the floating drag preview.
 * @param {() => DOMRect | undefined} [props.getCardBoundingRect] - Optional callback to return the card's DOMRect for drag positioning.
 * @param {React.ReactNode} props.children - Content of the draggable card.
 * @returns {JSX.Element} A draggable card component with full pointer and keyboard interaction support.
 */
export const DraggableCard = (_a) => {
    var { sx, moving, onSetMoving, onMoveCard, onPointerDragStart, onPointerDragMove, onPointerDragEnd, onPointerDragCancel, renderDragCard, getCardBoundingRect, children } = _a, props = __rest(_a, ["sx", "moving", "onSetMoving", "onMoveCard", "onPointerDragStart", "onPointerDragMove", "onPointerDragEnd", "onPointerDragCancel", "renderDragCard", "getCardBoundingRect", "children"]);
    const boxRef = React.useRef(null);
    const draggingPointerId = React.useRef(undefined);
    const [draggingPoint, setDraggingPoint] = React.useState();
    const [draggingSize, setDraggingSize] = React.useState();
    const cardSx = [
        {
            position: 'relative',
            backgroundColor: 'background.paper',
            border: '1px solid ' + themeColors.border.main,
            borderRadius: 1,
            outline: 'none',
            cursor: 'pointer',
            userSelect: 'none',
            opacity: draggingPoint ? 0 : 1,
            transform: moving ? rotate : 'none',
            '&:focus': {
                outline: '1px solid ' + themeColors.accent.blue.base,
            },
            touchAction: 'none',
            WebkitTapHighlightColor: 'transparent',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
    ];
    let dragCard = undefined;
    if (draggingPoint && draggingSize) {
        if (renderDragCard) {
            dragCard = renderDragCard(React.createElement(Box, { sx: [
                    ...cardSx,
                    {
                        opacity: 1,
                        outline: '1px solid ' + themeColors.accent.blue.base,
                    },
                ] }, children), {
                position: 'absolute',
                left: draggingPoint.x - draggingSize.offsetX,
                top: draggingPoint.y - draggingSize.offsetY,
                width: draggingSize.width,
                height: draggingSize.height,
                zIndex: (theme) => theme.zIndex.tooltip + 10,
                pointerEvents: 'none',
                transform: rotate,
            });
        }
        else {
            dragCard = (React.createElement(Box, { sx: [
                    ...cardSx,
                    {
                        opacity: 1,
                        position: 'absolute',
                        left: draggingPoint.x - draggingSize.offsetX,
                        top: draggingPoint.y - draggingSize.offsetY,
                        width: draggingSize.width,
                        height: draggingSize.height,
                        zIndex: (theme) => theme.zIndex.tooltip + 10,
                        pointerEvents: 'none',
                        outline: '1px solid ' + themeColors.accent.blue.base,
                        transform: rotate,
                    },
                ] }, children));
        }
    }
    const cancelDrag = React.useCallback(() => {
        var _a;
        draggingFlag = false;
        if (draggingPointerId.current != null) {
            (_a = boxRef.current) === null || _a === void 0 ? void 0 : _a.releasePointerCapture(draggingPointerId.current);
            draggingPointerId.current = undefined;
        }
        onPointerDragCancel === null || onPointerDragCancel === void 0 ? void 0 : onPointerDragCancel();
        setDraggingPoint(undefined);
        setDraggingSize(undefined);
    }, [onPointerDragCancel]);
    React.useEffect(() => {
        const blurListener = () => {
            if (draggingPointerId.current != null) {
                cancelDrag();
            }
        };
        const visibilityChangeListener = () => {
            if (draggingPointerId.current != null && document.visibilityState === 'hidden') {
                cancelDrag();
            }
        };
        window.addEventListener('blur', blurListener);
        document.addEventListener('visibilitychange', visibilityChangeListener);
        return () => {
            window.removeEventListener('blur', blurListener);
            document.removeEventListener('visibilitychange', visibilityChangeListener);
        };
    }, [cancelDrag]);
    return (React.createElement(React.Fragment, null,
        React.createElement(Box, { ref: boxRef, sx: cardSx, onPointerDown: (event) => {
                const point = { x: event.clientX, y: event.clientY };
                if (!draggingFlag && event.button === 0 && onPointerDragStart && onPointerDragStart(point)) {
                    draggingFlag = true;
                    draggingPointerId.current = event.pointerId;
                    const target = event.currentTarget;
                    target.focus();
                    target.setPointerCapture(event.pointerId);
                    let rect = undefined;
                    if (getCardBoundingRect) {
                        rect = getCardBoundingRect();
                    }
                    if (!rect) {
                        target.style.transform = 'none';
                        rect = target.getBoundingClientRect();
                        target.style.transform = '';
                    }
                    onSetMoving === null || onSetMoving === void 0 ? void 0 : onSetMoving(false);
                    setDraggingPoint(point);
                    setDraggingSize({
                        offsetX: point.x - rect.x,
                        offsetY: point.y - rect.y,
                        width: rect.width,
                        height: rect.height,
                    });
                    event.preventDefault();
                }
            }, onPointerMove: (event) => {
                var _a;
                if (draggingPointerId.current != null) {
                    if (event.buttons === 1) {
                        (_a = document.getSelection()) === null || _a === void 0 ? void 0 : _a.removeAllRanges();
                        const point = { x: event.clientX, y: event.clientY };
                        onPointerDragMove === null || onPointerDragMove === void 0 ? void 0 : onPointerDragMove(point);
                        setDraggingPoint(point);
                    }
                    else if (event.buttons === 0) {
                        // This code is here to fix a strange bug on some computers (Mac M1 Laptop, Chrome, refresh rate set to 60hz on retina display).
                        // Expected: user presses mouse button - we get pointerDown event
                        // Expected: user moves mouse -> we get pointerMove events, that indicate that button is pressed
                        // Expected: user releases mouse button -> we get pointerUp event
                        // Unexpected: user does nothing -> we get pointerDown event
                        // Unexpected: user moves mouse > we get pointerMove events, that show that no buttons are pressed
                        draggingFlag = false;
                        draggingPointerId.current = undefined;
                        onPointerDragEnd === null || onPointerDragEnd === void 0 ? void 0 : onPointerDragEnd({ x: event.clientX, y: event.clientY });
                        event.currentTarget.releasePointerCapture(event.pointerId);
                        setDraggingPoint(undefined);
                        setDraggingSize(undefined);
                    }
                }
            }, onPointerUp: (event) => {
                if (draggingPointerId.current != null && event.button === 0) {
                    draggingFlag = false;
                    draggingPointerId.current = undefined;
                    onPointerDragEnd === null || onPointerDragEnd === void 0 ? void 0 : onPointerDragEnd({ x: event.clientX, y: event.clientY });
                    event.currentTarget.releasePointerCapture(event.pointerId);
                    setDraggingPoint(undefined);
                    setDraggingSize(undefined);
                }
            }, onContextMenu: cancelDrag, tabIndex: 0, onKeyDown: (event) => {
                switch (event.key) {
                    case ' ':
                        if (!draggingPoint) {
                            onSetMoving === null || onSetMoving === void 0 ? void 0 : onSetMoving(!moving);
                            event.preventDefault();
                        }
                        break;
                    case 'ArrowLeft':
                        if (moving) {
                            onMoveCard === null || onMoveCard === void 0 ? void 0 : onMoveCard('left');
                            event.preventDefault();
                        }
                        break;
                    case 'ArrowRight':
                        if (moving) {
                            onMoveCard === null || onMoveCard === void 0 ? void 0 : onMoveCard('right');
                            event.preventDefault();
                        }
                        break;
                }
            }, onBlur: () => {
                if (moving) {
                    onSetMoving === null || onSetMoving === void 0 ? void 0 : onSetMoving(false);
                }
            }, "data-test": props['data-test'] }, children),
        dragCard && (React.createElement(Box, { sx: {
                position: 'fixed',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                zIndex: (theme) => theme.zIndex.tooltip + 9,
                pointerEvents: 'none',
            } }, dragCard))));
};
